import * as React from 'react';
import { Link } from 'react-router-dom';

import CloudOffOutlinedIcon from '@mui/icons-material/CloudOffOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import LocalOfferOutlinedIcon from '@mui/icons-material/LocalOfferOutlined';
import InsightsOutlinedIcon from '@mui/icons-material/InsightsOutlined';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import SyncOutlinedIcon from '@mui/icons-material/SyncOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import WindowIcon from '@mui/icons-material/Window';
import AppleIcon from '@mui/icons-material/Apple';

import AuthContext from '../../context/AuthContext';
import LandingHeader from './LandingHeader';
import { formatSize, useLatestRelease, useVisitorPlatform } from './useLatestRelease';
import styles from './Landing.module.css';

const footerLogo = `${process.env.PUBLIC_URL}/imagotipo-blanco.svg`;

const FEATURES = [
  { icon: <CloudOffOutlinedIcon />, title: 'Cobra sin internet', text: 'Las ventas se guardan en la caja y se sincronizan solas cuando vuelve la conexión. Tu negocio no se detiene.' },
  { icon: <ReceiptLongOutlinedIcon />, title: 'Tickets y varios recibos', text: 'Imprime en tu impresora térmica, atiende varias cuentas abiertas a la vez y personaliza el diseño del ticket.' },
  { icon: <Inventory2OutlinedIcon />, title: 'Inventario por sucursal', text: 'Existencias en tiempo real, alertas de stock bajo, ajustes con motivo e historial de cada movimiento.' },
  { icon: <PeopleAltOutlinedIcon />, title: 'Clientes que regresan', text: 'Registra a tus clientes al cobrar y conoce sus visitas, su historial de compras y cuánto gastan.' },
  { icon: <LocalOfferOutlinedIcon />, title: 'Promociones y cupones', text: 'Descuentos por porcentaje, monto o regalo, automáticos o con cupones de un solo uso para tus campañas.' },
  { icon: <InsightsOutlinedIcon />, title: 'Análisis y reporte del día', text: 'Ingresos, ticket promedio, productos estrella, ventas por sucursal y un corte diario listo para imprimir.' },
  { icon: <StorefrontOutlinedIcon />, title: 'Multisucursal y equipo', text: 'Varias sucursales y cajas, cuentas para tus cajeros y permisos de administrador para quien los necesite.' },
  { icon: <LocalShippingOutlinedIcon />, title: 'Proveedores y compras', text: 'Órdenes de compra que actualizan el inventario al recibirlas, con costos por proveedor.' },
  { icon: <SyncOutlinedIcon />, title: 'Panel web sincronizado', text: 'Lo que vendes en caja aparece en tu panel en segundos. Administra productos, precios y promociones desde cualquier lugar.' },
];

const STEPS = [
  { title: 'Crea tu cuenta', text: 'Regístrate gratis, crea tu organización y da de alta tu sucursal y tus productos desde el panel web.' },
  { title: 'Instala TRUE POS', text: 'Descarga la app para Windows o macOS en la computadora de tu caja.' },
  { title: 'Empieza a vender', text: 'Inicia sesión, elige la sucursal de esa caja y cobra. Todo se sincroniza con tu panel.' },
];

const SOON = [
  { title: 'Menú digital', text: 'Tu menú con fotos y videos en un QR y en pantallas de la tienda, con "agotado" en vivo.' },
  { title: 'Lealtad por WhatsApp', text: 'Visitas, recompensas y cupones automáticos, sin apps que descargar.' },
  { title: 'Pedidos en línea', text: 'Tus clientes piden desde la web y el pedido llega directo a la caja.' },
  { title: 'Facturación CFDI', text: 'Autofactura para tus clientes y factura global automática.' },
  { title: 'Android y iPad', text: 'TRUE POS en tablets, con la misma experiencia.' },
];

const FAQ = [
  { q: '¿Necesito internet para vender?', a: 'No. TRUE POS guarda cada venta en la computadora de la caja y la envía a tu panel en cuanto hay conexión. Solo necesitas internet para iniciar sesión la primera vez y para sincronizar.' },
  { q: '¿Qué impresora necesito?', a: 'Cualquier impresora térmica de tickets compatible con ESC/POS (58 u 80 mm) conectada por USB o por red. Te recomendamos una de red: sirve para Windows, macOS y, próximamente, tablets.' },
  { q: '¿Cómo se actualiza?', a: 'Solo. Cuando hay una versión nueva, TRUE POS la descarga en segundo plano y la instala la próxima vez que cierras la app; nunca se reinicia a mitad de una venta. En macOS, por ahora, te avisa y te trae a esta página para descargarla.' },
  { q: '¿En qué computadoras funciona?', a: 'Windows 10 u 11 de 64 bits y macOS 11 o posterior (Apple Silicon e Intel). La versión para Android y iPad está en camino.' },
  { q: 'Windows o macOS me muestran un aviso al instalar', a: 'Es normal mientras terminamos de firmar el instalador. En Windows: "Más información" → "Ejecutar de todos modos". En macOS: abre la app con clic derecho → "Abrir", o en Configuración del Sistema → Privacidad y seguridad → "Abrir de todos modos".' },
];

const Landing = () => {
  const { user } = React.useContext(AuthContext);
  const { release, status } = useLatestRelease();
  const { platform, macArch } = useVisitorPlatform();

  React.useEffect(() => {
    document.title = 'TRUE POS — Punto de venta que sigue cobrando sin internet';
    // Honor #descargar / #funciones when arriving from another page.
    if (window.location.hash) document.querySelector(window.location.hash)?.scrollIntoView();
  }, []);

  const win = release?.windows?.x64;
  const mac = release?.mac?.[macArch] ?? release?.mac?.arm64 ?? release?.mac?.x64;
  const otherMac = macArch === 'arm64' ? release?.mac?.x64 : release?.mac?.arm64;
  const primary = platform === 'windows' ? win : platform === 'mac' ? mac : undefined;
  const primaryLabel = platform === 'windows' ? 'Descargar para Windows' : 'Descargar para macOS';

  return (
    <>
      <LandingHeader signedIn={!!user} />
      <main className={styles.page}>
        {/* Hero */}
        <section className={styles.hero}>
          <div>
            <span className={styles.eyebrow}>Punto de venta para cafeterías y comercios</span>
            <h1 className={styles.heroTitle}>
              Vende sin interrupciones con <span className={styles.highlight}>TRUE POS</span>
            </h1>
            <p className={styles.heroSubtitle}>
              Cobra aunque se caiga el internet, controla tu inventario en cada sucursal y convierte
              cada venta en un cliente que regresa. Todo conectado a tu panel en la nube.
            </p>
            <div className={styles.heroActions}>
              {primary ? (
                <a href={primary.url} className={styles.primaryButton}>
                  <DownloadIcon /> {primaryLabel}
                </a>
              ) : (
                <a href="#descargar" className={styles.primaryButton}>
                  <DownloadIcon /> Descargar TRUE POS
                </a>
              )}
              <Link to={user ? '/admin' : '/register'} className={styles.secondaryButton}>
                {user ? 'Ir al panel' : 'Crear cuenta gratis'} <ArrowForwardIcon fontSize="small" />
              </Link>
            </div>
            <p className={styles.heroMeta}>
              {release ? `Versión ${release.version} · ` : ''}Disponible para Windows y macOS ·{' '}
              <a href="#descargar">Todas las descargas</a>
            </p>
          </div>

          {/* Illustrative mockup of the sale screen (static, sample data). */}
          <div className={styles.mockup} aria-hidden="true">
            <div className={styles.mockBar}>
              <span>TRUE · POS</span>
              <span className={styles.mockStatus}><span className={styles.dot} /> En línea</span>
            </div>
            <div className={styles.mockBody}>
              <div className={styles.mockSide}>
                <span className={styles.mockNavActive}>Venta</span>
                <span className={styles.mockNav}>Historial</span>
                <span className={styles.mockNav}>Clientes</span>
                <span className={styles.mockNav}>Productos</span>
                <span className={styles.mockNav}>Inventario</span>
              </div>
              <div className={styles.mockMain}>
                <div className={styles.mockSearch}>Escanea o busca un producto…</div>
                <div className={styles.mockTable}>
                  <div className={styles.mockHead}><span>Producto</span><span className={styles.right}>Cant.</span><span className={styles.right}>Importe</span></div>
                  <div className={styles.mockRow}><span>Latte 12 oz<br /><span className={styles.mockMuted}>Leche de avena</span></span><span className={styles.right}>2</span><span className={styles.right}>$110.00</span></div>
                  <div className={styles.mockRow}><span>Croissant de mantequilla</span><span className={styles.right}>1</span><span className={styles.right}>$45.00</span></div>
                  <div className={styles.mockRow}><span>Cold brew 16 oz</span><span className={styles.right}>1</span><span className={styles.right}>$65.00</span></div>
                  <div className={styles.mockRow}><span className={styles.mockMuted}>Promo horas bajas −10%</span><span /><span className={`${styles.right} ${styles.mockMuted}`}>−$22.00</span></div>
                </div>
                <div className={styles.mockCheckout}>
                  <div>
                    <div className={styles.mockMuted}>Total · 4 artículos</div>
                    <div className={styles.mockTotal}>$198.00</div>
                  </div>
                  <span className={styles.mockPay}>Cobrar</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features */}
        <section id="funciones" className={styles.sectionWhite}>
          <div className={styles.container}>
            <h2 className={styles.sectionTitle}>Todo lo que tu caja necesita</h2>
            <p className={styles.sectionSubtitle}>
              Una app de punto de venta en la caja y un panel web para administrar tu negocio, conectados entre sí.
            </p>
            <div className={styles.featuresGrid}>
              {FEATURES.map(f => (
                <div key={f.title} className={styles.featureCard}>
                  <div className={styles.featureIcon}>{f.icon}</div>
                  <h3 className={styles.featureTitle}>{f.title}</h3>
                  <p className={styles.featureDescription}>{f.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className={styles.section}>
          <div className={styles.container}>
            <h2 className={styles.sectionTitle}>Empieza en minutos</h2>
            <p className={styles.sectionSubtitle}>Sin instalaciones complicadas ni servidores en tu negocio.</p>
            <div className={styles.steps}>
              {STEPS.map((s, i) => (
                <div key={s.title} className={styles.step}>
                  <span className={styles.stepNumber}>{i + 1}</span>
                  <h3 className={styles.featureTitle}>{s.title}</h3>
                  <p className={styles.featureDescription}>{s.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Downloads */}
        <section id="descargar" className={styles.sectionWhite}>
          <div className={styles.container}>
            <h2 className={styles.sectionTitle}>Descarga TRUE POS</h2>
            <p className={styles.sectionSubtitle}>
              Instálalo en la computadora de tu caja e inicia sesión con tu cuenta de TRUE.
            </p>
            <div className={styles.downloads}>
              <div className={platform === 'windows' ? styles.downloadCardActive : styles.downloadCard}>
                <div className={styles.downloadHead}>
                  <div className={styles.featureIcon} style={{ marginBottom: 0 }}><WindowIcon /></div>
                  <div>
                    <h3 className={styles.downloadName}>Windows</h3>
                    <p className={styles.downloadReq}>Windows 10 u 11 · 64 bits{win?.size ? ` · ${formatSize(win.size)}` : ''}</p>
                  </div>
                </div>
                <a
                  href={win?.url ?? '#descargar'}
                  className={`${styles.primaryButton} ${win ? '' : styles.disabledButton}`}
                  aria-disabled={!win}
                >
                  <DownloadIcon /> {win ? 'Descargar instalador (.exe)' : status === 'loading' ? 'Buscando la última versión…' : 'Disponible muy pronto'}
                </a>
                <p className={styles.installNote}>
                  Si aparece "Windows protegió tu PC", elige <strong>Más información → Ejecutar de todos modos</strong>.
                </p>
              </div>

              <div className={platform === 'mac' ? styles.downloadCardActive : styles.downloadCard}>
                <div className={styles.downloadHead}>
                  <div className={styles.featureIcon} style={{ marginBottom: 0 }}><AppleIcon /></div>
                  <div>
                    <h3 className={styles.downloadName}>macOS</h3>
                    <p className={styles.downloadReq}>
                      macOS 11 o posterior · {macArch === 'arm64' ? 'Apple Silicon' : 'Intel'}{mac?.size ? ` · ${formatSize(mac.size)}` : ''}
                    </p>
                  </div>
                </div>
                <a
                  href={mac?.url ?? '#descargar'}
                  className={`${styles.primaryButton} ${mac ? '' : styles.disabledButton}`}
                  aria-disabled={!mac}
                >
                  <DownloadIcon /> {mac ? 'Descargar para Mac (.dmg)' : status === 'loading' ? 'Buscando la última versión…' : 'Disponible muy pronto'}
                </a>
                {otherMac && (
                  <span className={styles.altLink}>
                    ¿Tu Mac es {macArch === 'arm64' ? 'Intel' : 'Apple Silicon'}? <a href={otherMac.url}>Descarga esta versión</a>
                  </span>
                )}
                <p className={styles.installNote}>
                  Si macOS no la abre, haz <strong>clic derecho → Abrir</strong> la primera vez.
                </p>
              </div>
            </div>
            {release && (
              <p className={styles.releaseInfo}>
                Versión {release.version}
                {release.releasedAt ? ` · publicada el ${new Date(release.releasedAt).toLocaleDateString('es-MX', { day: 'numeric', month: 'long', year: 'numeric' })}` : ''}
              </p>
            )}
          </div>
        </section>

        {/* Coming soon */}
        <section className={styles.section}>
          <div className={styles.container}>
            <h2 className={styles.sectionTitle}>Lo que viene</h2>
            <p className={styles.sectionSubtitle}>TRUE POS crece con los negocios que lo usan. Esto es lo próximo:</p>
            <div className={styles.soonGrid}>
              {SOON.map(s => (
                <div key={s.title} className={styles.soonCard}>
                  <span className={styles.badge}>Próximamente</span>
                  <h3>{s.title}</h3>
                  <p>{s.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className={styles.sectionWhite}>
          <div className={styles.container}>
            <h2 className={styles.sectionTitle}>Preguntas frecuentes</h2>
            <div className={styles.faq} style={{ marginTop: '2.5rem' }}>
              {FAQ.map(item => (
                <details key={item.q} className={styles.faqItem}>
                  <summary>{item.q}</summary>
                  <p>{item.a}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className={styles.cta}>
          <div className={styles.ctaContent}>
            <h2 className={styles.ctaTitle}>¿Listo para vender con TRUE POS?</h2>
            <p className={styles.ctaSubtitle}>Crea tu cuenta gratis, da de alta tus productos y empieza a cobrar hoy mismo.</p>
            <Link to={user ? '/admin' : '/register'} className={styles.ctaButton}>
              {user ? 'Ir al panel' : 'Crear cuenta gratis'} <ArrowForwardIcon />
            </Link>
          </div>
        </section>
      </main>

      <footer className={styles.footer}>
        <div className={styles.footerInner}>
          <div>
            <img src={footerLogo} alt="TRUE" className={styles.footerLogo} />
            <p className={styles.footerText}>Innovación y excelencia en ingeniería de datos</p>
          </div>
          <div className={styles.footerLinks}>
            <a href="#funciones">Funciones</a>
            <a href="#descargar">Descargar</a>
            <Link to="/login">Iniciar sesión</Link>
            <a href="https://truedata.com.mx" target="_blank" rel="noreferrer">truedata.com.mx</a>
          </div>
          <p className={styles.footerText}>
            Copyright © <a href="https://truedata.com.mx" target="_blank" rel="noreferrer">TDSE</a> {new Date().getFullYear()}
          </p>
        </div>
      </footer>
    </>
  );
};

export default Landing;
